import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { toast } from "sonner";

import DashboardLayout from "@/components/layout/DashboardLayout";
import StripeConnectBanner from "@/components/StripeConnectBanner";
import { Button } from "@/components/ui/button";
import Typography from "@/components/ui/typography";
import LicenseeContent from "@/features/founders/components/LicenseeContent";
import {
  getInvoices,
  getStripeAccountStatus,
  InvoicesKeys,
} from "@/features/invoices/api/invoices";
import InvoiceFormSheet from "@/features/invoices/components/invoice/InvoiceFormSheet";
import RecurringInvoiceDrawer from "@/features/invoices/components/recurring/RecurringInvoiceDrawer";
import RecurringInvoiceSection from "@/features/invoices/components/recurring/RecurringInvoiceSection";
import StripeRestrictedBanner from "@/features/invoices/components/StripeRestrictedBanner";
import { Invoice } from "@/features/invoices/types/invoices";
import { useStripeStatus } from "@/hooks/useStripeStatus";
import { track } from "@/utils/mixpanel";

const Invoices = () => {
  const { stripeActive, stripeConnectUrl } = useStripeStatus();
  const [isInvoiceFormOpen, setIsInvoiceFormOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<
    string | undefined
  >(undefined);
  const [selectedRecurringInvoiceId, setSelectedRecurringInvoiceId] = useState<
    string | null
  >(null);
  const [recurringDrawerStartInEdit, setRecurringDrawerStartInEdit] =
    useState(false);

  const { data: invoices, isLoading: invoicesLoading } = useQuery({
    queryKey: [InvoicesKeys.Invoices],
    queryFn: getInvoices,
  });

  const { data: stripeAccountStatus } = useQuery({
    queryKey: [InvoicesKeys.StripeAccountStatus],
    queryFn: getStripeAccountStatus,
    enabled: stripeActive,
    refetchInterval: (query) =>
      query.state.data?.pendingVerification ? 15_000 : false,
  });

  const handleGenerateInvoice = () => {
    // Reset state for creating a new invoice
    setSelectedInvoiceId(undefined);
    setIsInvoiceFormOpen(true);
    track("start_invoice_generation");
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setSelectedInvoiceId(invoice.id);
    setIsInvoiceFormOpen(true);
  };

  const handleInvoiceFormSubmit = () => {
    toast.success("Invoice generated successfully!", {
      description: `Invoice has been generated and is ready to download.`,
    });
  };

  const handleDownloadInvoice = (invoiceId: string) => {
    // Find the invoice with the matching ID
    const invoice = invoices?.find((inv) => inv.id === invoiceId);

    if (invoice?.pdfUrl) {
      // Track download action from table
      track("download_invoice", {
        invoice_id: invoice.id,
        invoice_number: invoice.number,
        source: "table",
        status: invoice.status,
      });

      // Open the PDF URL in a new tab
      window.open(invoice.pdfUrl, "_blank");
      toast.success(`Invoice ${invoice.number} downloaded`, {
        description: "The invoice has been opened in a new tab.",
      });
    } else {
      toast.error("PDF not available", {
        description: "This invoice doesn't have a downloadable PDF.",
      });
    }
  };

  const handleRecurringInvoiceClick = (recurringInvoiceId: string) => {
    setSelectedRecurringInvoiceId(recurringInvoiceId);
    setRecurringDrawerStartInEdit(false);
  };

  const handleRecurringInvoiceEdit = (recurringInvoiceId: string) => {
    setSelectedRecurringInvoiceId(recurringInvoiceId);
    setRecurringDrawerStartInEdit(true);
  };

  const handleRecurringDrawerClose = () => {
    setSelectedRecurringInvoiceId(null);
    setRecurringDrawerStartInEdit(false);
  };

  const handleIssuedInvoiceClick = (invoiceId: string) => {
    // Close the recurring drawer and open the invoice form/detail
    setSelectedRecurringInvoiceId(null);
    setSelectedInvoiceId(invoiceId);
    setIsInvoiceFormOpen(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <Typography size="h2" className="text-[#1e1e24]">
              Invoices
            </Typography>
            <Typography size="b2" className="text-[#767677]">
              Create and manage your invoices.
            </Typography>
          </div>
          <Button
            variant="ghost"
            className={`h-9 rounded-lg px-5 border border-[#e8e8e8] text-[#1e1e24] hover:bg-transparent whitespace-nowrap ${stripeAccountStatus?.restrictedForPayments ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={handleGenerateInvoice}
            disabled={stripeAccountStatus?.restrictedForPayments}
          >
            <Plus className="mr-2 h-4 w-4" /> Create invoice
          </Button>
        </div>

        {/* Stripe Connect Banner - shown when not connected */}
        {!stripeActive && (
          <StripeConnectBanner connectUrl={stripeConnectUrl} />
        )}

        {stripeAccountStatus?.isRestricted && (
          <StripeRestrictedBanner
            status={stripeAccountStatus.status}
            pendingVerification={stripeAccountStatus.pendingVerification}
          />
        )}

        <RecurringInvoiceSection
          onViewDetails={handleRecurringInvoiceClick}
          onEdit={handleRecurringInvoiceEdit}
        />

        <div className="bg-[#f9f9f9] rounded-2xl p-6">
          <div className="text-sm font-semibold text-[#767677]">
            All Invoices
          </div>
          <div className="bg-[#fdfdfd] p-2 mt-4">
            <LicenseeContent
              invoices={invoices || []}
              isLoading={invoicesLoading}
              onDownloadInvoice={handleDownloadInvoice}
              onInvoiceRowClick={handleEditInvoice}
              onRecurringIconClick={handleRecurringInvoiceClick}
            />
          </div>
        </div>
      </div>

      <RecurringInvoiceDrawer
        open={!!selectedRecurringInvoiceId}
        onOpenChange={(open) => {
          if (!open) {
            handleRecurringDrawerClose();
          }
        }}
        recurringInvoiceId={selectedRecurringInvoiceId}
        startInEdit={recurringDrawerStartInEdit}
        onInvoiceClick={handleIssuedInvoiceClick}
      />

      <InvoiceFormSheet
        open={isInvoiceFormOpen}
        onOpenChange={setIsInvoiceFormOpen}
        onSubmit={handleInvoiceFormSubmit}
        existingInvoiceId={selectedInvoiceId}
        onViewRecurringInvoice={(recurringInvoiceId) => {
          // Close the invoice form and open the recurring invoice drawer
          setIsInvoiceFormOpen(false);
          setSelectedInvoiceId(undefined);
          setSelectedRecurringInvoiceId(recurringInvoiceId);
          setRecurringDrawerStartInEdit(false);
        }}
      />
    </DashboardLayout>
  );
};

export default Invoices;
